import { debounce } from "Helper/functionHelper/decorators";
import { Component, ComponentArgs } from "@kluntje/core";
import { OPEN, ACTIVE, INACTIVE } from "Helper/cssClasses";
import { addClass, hasClass, removeClass, toggleClass } from "@kluntje/js-utils/lib/dom-helpers";

/**
 * Custom Element to display options in dropdown fashion
 */
export class FRADropdown extends Component {
  constructor({ ui = {}, events = [], initialStates = {}, reactions = {} }: ComponentArgs = {}) {
    super({
      ui: {
        toggleBtn: ".fra-js-dropdown__toggle :-one",
        label: ".fra-js-dropdown__label :-one",
        hiddenInput: ".fra-js-dropdown__input :-one",
        option: ".fra-js-dropdown__item input",
        resetButton: ".fra-js-dropdown__reset :-one",
      },

      events: [
        {
          event: "click",
          target: "toggleBtn",
          handler: "toggleDropdown",
        },
        {
          event: "click",
          target: "option",
          handler: "selectOption",
        },
        {
          event: "click",
          target: "resetButton",
          handler: "reset",
        },
        {
          event: "fra-open-dropdown",
          target: "window",
          handler: "closeDropdown",
        },
        {
          event: "keydown",
          target: "toggleBtn",
          handler: "handleKeyboard",
        },
      ],
      initialStates: {
        selectedOption: null,
        formValue: [],
        defaultValue: "",
        defaultLabel: "",
        selectedOptionValue: "",
        multiple: null,
        hasCTA: false,
      },
      reactions: {
        formValue: ["updateDisplay"],
      },
    });
    Object.assign(this.uiDefinitions, ui);
    Object.assign(this.initialStates, initialStates);
    Object.assign(this.reactions, reactions);

    this.mergeEvents(events);
  }

  /**
   * @override
   * Store default label and value in state
   */
  afterComponentRender(): void {
    // render dropdown finished
    this.setState({
      defaultValue: this.value || "",
      defaultLabel: this.label,
      multiple: hasClass(this, "fra-js-dropdown--multiple"),
    });

    const initOption = this.selectedOptionValue;
    if (initOption) {
      let foundOption: { click?: any; value?: string };
      this.ui.option.forEach((op: { value: string }) => {
        if (!foundOption && op.value === initOption) {
          foundOption = op;
        }
      });
      if (foundOption) {
        foundOption.click();
      }
    }
  }

  /**
   * @returns {string} current dropdown label text
   */
  get label(): string {
    return this.ui.label.textContent;
  }

  /**
   * @returns {string} initial option value
   */
  get selectedOptionValue(): string {
    return this.getAttribute("data-selected-option-value") || "";
  }

  /**
   * @returns {string} current dropdown value
   */
  get value(): string {
    let val: string;
    if (this.state.formValue.length > 1) {
      val = this.state.formValue.join(",");
    } else {
      val = this.state.formValue[0];
    }
    return val;
  }

  /**
   * Add new value to state (or remove if already exisiting)
   * @param {string} newValue
   */
  set value(newValue: string) {
    let currentStateValue;
    // dropdown allows multiple selections
    if (this.state.multiple && this.state.formValue.length > 0) {
      // create copy to manipulate
      currentStateValue = this.state.formValue.slice();
      // check whether newValue is already in state
      const currentIndex: number = currentStateValue.indexOf(newValue);
      if (currentIndex > -1) {
        // value is already in state: remove it
        currentStateValue.splice(currentIndex, 1);
      } else {
        // add new value to state
        currentStateValue.push(newValue);
      }
    } else {
      // replace current vlaue with new value
      currentStateValue = [newValue];
    }
    this.setState({ formValue: currentStateValue });
  }

  /**
   * Returns provided option-value from element
   * @param {HTMLElement} option
   * @returns {string}
   */
  getOptionValue(option: HTMLElement): string {
    return option.getAttribute("value") || "";
  }

  /**
   * Returns provided option-label from element
   * @param {HTMLElement} option
   * @returns {string}
   */
  getOptionLabel(option: HTMLElement): string {
    return option.getAttribute("data-option-label") || "";
  }

  /**
   * close dropdown on option select or if other dropdown was opened recently
   * @param {CustomEvent} [e]
   */
  closeDropdown(e?: CustomEvent): void {
    // check whether global event recieved stems from this dropdown and disregard it
    if (e && e.detail === this) {
      return;
    }
    // all clear, dropdown should actually close
    this.ui.toggleBtn.blur();
    removeClass(this, OPEN);
  }

  /**
   * toggle dropdown class on dropdown CTA click
   */
  toggleDropdown(): void {
    if (!hasClass(this, INACTIVE)) {
      // dispatch event to notify listeners of newly opened dropdown
      if (!hasClass(this, OPEN)) {
        this.updateDisplay();
        window.dispatchEvent(new CustomEvent("fra-open-dropdown", { detail: this }));
        this.ui.toggleBtn.focus();
      }
      // toggle open state
      toggleClass(this, OPEN);
    }
  }

  /**
   * Show reset button in dropdown
   */
  activateResetButton(): void {
    // toggle active state
    addClass(this.ui.resetButton, ACTIVE);
  }
  /**
   * Hide reset button in dropdown
   */
  deactivateResetButton(): void {
    // toggle active state
    removeClass(this.ui.resetButton, ACTIVE);
  }

  /**
   * set input value, change label text and close dropdown on option click
   * @param {Event} e - click event
   */
  selectOption(e: Event): void {
    e.preventDefault();
    const optionSelected = e.target as HTMLInputElement;
    if (this.state.hasCTA) {
      this.value = this.getOptionValue(optionSelected);
    } else {
      // set values
      this.setInputValue(this.getOptionValue(optionSelected));
      this.setDropdownLabel(this.getOptionLabel(optionSelected));
      // close dropdown now
      this.closeDropdown();
    }
  }

  /**
   * set input value
   * @param {String} value
   */
  setInputValue(value: string): void {
    this.value = value;
    this.ui.hiddenInput.value = this.value;
    // force change event on hidden input
    this.dispatchEvent(new Event("change"));
  }

  /**
   * set label text
   * @param {String} label
   */
  setDropdownLabel(label: string): void {
    this.ui.label.textContent = label;
    if (label !== this.state.defaultLabel) {
      this.activateResetButton();
    } else {
      this.deactivateResetButton();
    }
  }

  /**
   * Set displayed label from option value
   * @param {string} value - Value for which the correct label should be displayed
   */
  setDropdownLabelByValue(value: string): void {
    this.ui.option.forEach((element: HTMLInputElement) => {
      if (value === this.getOptionValue(element)) {
        this.setDropdownLabel(this.getOptionLabel(element));
        return;
      }
    });
  }

  /**
   * Reset dropdown value and label
   */
  reset(): void {
    this.value = "";
    this.setInputValue(this.state.defaultValue);
    this.setDropdownLabel(this.state.defaultLabel);
    this.updateDisplay();
  }

  @debounce(200)
  /**
   * Update display of options to current state
   */
  updateDisplay(): void {
    // set selected state on option if available
    let currValues: string[];
    if (this.state.formValue.length > 0) {
      currValues = this.state.formValue.slice();
    } else {
      currValues = [];
    }
    this.ui.option.forEach((element: HTMLInputElement) => {
      element.checked = currValues.indexOf(this.getOptionValue(element)) > -1;
    });
  }

  /**
   * Needs to be overridden by actual implementation of multiple select component
   * @param {MouseEvent} e
   */
  handleApplySelection(e: MouseEvent): void {
    e.preventDefault();
  }

  /**
   * Keyboard navigation
   * @param {KeyboardEvent} e Captured KeyboardEvent
   */
  handleKeyboard(e: KeyboardEvent): void {
    // capture return, esc, arrow up, arrow down
    if ([13, 27, 38, 40].indexOf(e.keyCode) < 0) {
      return;
    }
    // get currently activated item
    const currentFocus = [...this.ui.option].findIndex(current => hasClass(current, ACTIVE));
    // a link has already gained focus
    if (currentFocus !== -1) {
      let newFocus = currentFocus;
      if (e.keyCode === 40 && currentFocus < this.ui.option.length - 1) {
        // go down
        newFocus++;
      } else if (e.keyCode === 38 && currentFocus > 0) {
        // go up
        newFocus--;
      }
      if (newFocus !== currentFocus) {
        removeClass(this.ui.option[currentFocus], ACTIVE);
        addClass(this.ui.option[newFocus], ACTIVE);
        this.setState({ selectedOption: newFocus });
      }
    } else {
      // add active class to first item in list
      addClass(this.ui.option[0], ACTIVE);
      this.setState({ selectedOption: 0 });
    }
    // handle enter/return
    if (e.keyCode === 13 && hasClass(this, OPEN) && this.state.selectedOption !== null) {
      this.ui.option[this.state.selectedOption].click();
    }
    // handle esc
    if (e.keyCode === 27) {
      this.closeDropdown();
    }
  }
}

customElements.define("fra-e-dropdown", FRADropdown);

export default FRADropdown;
